import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatPrice, formatTime, newIdempotencyKey } from "@/lib/format";
import { Container, PageHero, Seo } from "@/components/site/Ui";
import { MonthCalendar, SlotPicker } from "@/components/booking/Pickers";
import { Loading } from "./Catalog";

const EMAIL_LABEL = { sent: "Email di conferma inviata.", simulated: "Invio email disattivato in questo ambiente di prova: conserva il link qui sotto.", failed: "Non siamo riusciti a inviare l'email di conferma, ma la prenotazione è registrata. Puoi riprovare dalla pagina di gestione.", blocked: "Invio email non consentito in ambiente di test: conserva il link qui sotto." };

function Steps({ steps, current }) {
  return (
    <ol className="mb-10 flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label="Passaggi della prenotazione">
      {steps.map((s, i) => (
        <li key={s} aria-current={i === current ? "step" : undefined} className={`flex items-center gap-2 ${i === current ? "font-semibold text-[var(--petrol)]" : i < current ? "text-[var(--ink)]" : "text-[var(--muted-ink)]"}`}>
          <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-xs ${i < current ? "border-[var(--petrol)] bg-[var(--petrol)] text-white" : "border-current"}`}>{i < current ? <Check size={12} /> : i + 1}</span>{s}
        </li>
      ))}
    </ol>
  );
}

function CustomerForm({ value, onChange, onNext }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onNext(); }} className="grid max-w-2xl gap-5" data-testid="booking-customer-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="text-sm">Nome<input required maxLength={80} autoComplete="given-name" className="field mt-1" value={value.first_name} onChange={set("first_name")} data-testid="booking-first-name" /></label>
        <label className="text-sm">Cognome<input required maxLength={80} autoComplete="family-name" className="field mt-1" value={value.last_name} onChange={set("last_name")} data-testid="booking-last-name" /></label>
        <label className="text-sm">Email<input required type="email" autoComplete="email" className="field mt-1" value={value.email} onChange={set("email")} data-testid="booking-email" /></label>
        <label className="text-sm">Telefono<input required pattern="[0-9+ ()\-]{6,30}" autoComplete="tel" className="field mt-1" value={value.phone} onChange={set("phone")} data-testid="booking-phone" /></label>
      </div>
      <label className="text-sm">Note organizzative (facoltative)<textarea rows={3} maxLength={500} className="field mt-1" value={value.notes} onChange={set("notes")} data-testid="booking-notes" />
        <span className="mt-1 block text-xs text-[var(--muted-ink)]">Non inserire informazioni sulla salute: verranno raccolte in visita.</span></label>
      <label className="flex gap-3 text-sm"><input type="checkbox" required checked={value.privacy_ack} onChange={set("privacy_ack")} data-testid="booking-privacy" /> <span>Ho letto l'<Link to="/privacy" className="underline">informativa privacy</Link> (obbligatorio).</span></label>
      <label className="flex gap-3 text-sm"><input type="checkbox" checked={value.marketing_consent} onChange={set("marketing_consent")} data-testid="booking-marketing" /> <span>Desidero ricevere comunicazioni informative e promozionali (facoltativo).</span></label>
      <button className="btn-primary w-fit" data-testid="booking-customer-next">Continua al riepilogo</button>
    </form>
  );
}

function Done({ result }) {
  const a = result.appointment;
  const link = `${window.location.origin}/prenotazione/gestisci#t=${result.manage_token}`;
  return (
    <div className="card-soft max-w-2xl p-8" role="status" data-testid="booking-success">
      <p className="eyebrow">Prenotazione confermata</p>
      <h2 className="mt-3 text-4xl">Ti aspettiamo, {a.first_name}.</h2>
      <p className="mt-4">{a.treatment_name} · <span className="capitalize">{formatDate(a.start)}</span> alle {formatTime(a.start)}</p>
      <p className="mt-2 text-sm">Codice prenotazione: <strong data-testid="booking-ref">{a.ref}</strong></p>
      <p className={`mt-6 rounded-xl p-4 text-sm ${a.email_status === "failed" ? "bg-amber-50 text-amber-900" : "bg-[var(--powder-light)]"}`} data-testid="booking-email-status">{EMAIL_LABEL[a.email_status] || ""}</p>
      <p className="mt-6 text-sm">Link personale per spostare o annullare (non condividerlo):</p>
      <a href={link} className="mt-1 block break-all text-sm text-[var(--petrol)] underline" data-testid="booking-manage-link">{link}</a>
    </div>
  );
}

export default function Booking() {
  const [params] = useSearchParams();
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState({ treatment: null, staff: "", date: null, slot: null });
  const [customer, setCustomer] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "", privacy_ack: false, marketing_consent: false });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const idem = useRef(newIdempotencyKey());
  useEffect(() => {
    api.get("/public/booking/options").then((r) => {
      setOpts(r.data);
      const pre = r.data.treatments.find((t) => t.id === params.get("t"));
      if (pre) { setSel((s) => ({ ...s, treatment: pre })); setStep(r.data.allow_staff_choice ? 1 : 2); }
    });
  }, [params]);
  if (!opts) return <Loading />;
  const withStaff = opts.allow_staff_choice;
  const labels = ["Trattamento", ...(withStaff ? ["Professionista"] : []), "Data e ora", "I tuoi dati", "Conferma"];
  const idx = (s) => (withStaff ? s : s === 0 ? 0 : s - 1);
  const go = (s) => { setError(null); setStep(s); };
  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      const { privacy_ack, marketing_consent, ...c } = customer;
      const r = await api.post("/public/booking", { treatment_id: sel.treatment.id, staff_id: sel.staff || null, start: sel.slot.start, customer: c, privacy_ack, marketing_consent, idempotency_key: idem.current, website: "" });
      setResult(r.data);
    } catch (e) {
      setError(errorMessage(e));
      if (e.response?.status === 409) { idem.current = newIdempotencyKey(); setSel((s) => ({ ...s, slot: null })); }
    } finally { setBusy(false); }
  };
  return (
    <>
      <Seo title="Prenota" description="Prenota online un trattamento o una consulenza, senza registrazione." path="/prenota" />
      <PageHero eyebrow="Prenotazione online" title="Prenota il tuo appuntamento" intro="Nessun account richiesto. Le disponibilità mostrate sono in tempo reale." crumbs={[{ label: "Prenota" }]} />
      <Container className="py-16">
        {!opts.enabled ? <p data-testid="booking-disabled">La prenotazione online è momentaneamente sospesa. Contattaci telefonicamente.</p> : result ? <Done result={result} /> : (
          <>
            <Steps steps={labels} current={idx(step)} />
            {step > 0 && <button onClick={() => go(step === 2 && !withStaff ? 0 : step - 1)} className="mb-6 inline-flex items-center gap-1 text-sm text-[var(--petrol)]" data-testid="booking-back"><ArrowLeft size={16} /> Indietro</button>}
            {step === 0 && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="booking-treatments">
                {opts.treatments.map((t) => (
                  <button key={t.id} onClick={() => { setSel({ treatment: t, staff: "", date: null, slot: null }); go(withStaff ? 1 : 2); }} className="card-soft p-6 text-left" data-testid={`booking-treatment-${t.slug}`}>
                    <p className="eyebrow">{t.category_name}</p><p className="mt-2 font-serif text-2xl">{t.name}</p>
                    <p className="mt-3 text-sm text-[var(--muted-ink)]">{t.duration_min} min · {formatPrice(t.price_type, t.price_cents)}</p>
                  </button>
                ))}
              </div>
            )}
            {step === 1 && (
              <div className="grid gap-4 md:grid-cols-3" data-testid="booking-staff">
                {[{ id: "", name: "Nessuna preferenza", title: "Primo professionista disponibile" }, ...opts.staff.filter((s) => !sel.treatment.staff_ids.length || sel.treatment.staff_ids.includes(s.id))].map((s) => (
                  <button key={s.id || "any"} onClick={() => { setSel({ ...sel, staff: s.id, date: null, slot: null }); go(2); }} className="card-soft p-6 text-left" data-testid={`booking-staff-${s.id || "any"}`}><p className="font-serif text-2xl">{s.name}</p><p className="mt-1 text-sm text-[var(--muted-ink)]">{s.title}</p></button>
                ))}
              </div>
            )}
            {step === 2 && (
              <div className="grid gap-10 lg:grid-cols-2">
                <MonthCalendar treatmentId={sel.treatment.id} staffId={sel.staff} value={sel.date} maxDays={opts.max_days_ahead} onChange={(d) => setSel({ ...sel, date: d, slot: null })} />
                <div>
                  <p className="font-serif text-2xl">{sel.date ? <span className="capitalize">{formatDate(`${sel.date}T12:00:00Z`)}</span> : "Orari"}</p>
                  <p className="mb-5 mt-1 text-xs text-[var(--muted-ink)]">Orari espressi nel fuso di Roma.</p>
                  <SlotPicker treatmentId={sel.treatment.id} staffId={sel.staff} date={sel.date} value={sel.slot} onChange={(s) => setSel({ ...sel, slot: s })} />
                  <button className="btn-primary mt-8" disabled={!sel.slot} onClick={() => go(3)} data-testid="booking-slot-next">Continua</button>
                </div>
              </div>
            )}
            {step === 3 && <CustomerForm value={customer} onChange={setCustomer} onNext={() => go(4)} />}
            {step === 4 && (
              <div className="card-soft max-w-2xl p-8" data-testid="booking-summary">
                <p className="eyebrow">Riepilogo</p>
                <dl className="mt-6 space-y-3 text-sm">
                  {[["Trattamento", sel.treatment.name], ["Data", formatDate(sel.slot.start)], ["Ora", formatTime(sel.slot.start)], ["Durata", `${sel.treatment.duration_min} minuti`], ["Prezzo", formatPrice(sel.treatment.price_type, sel.treatment.price_cents)], ["Nome", `${customer.first_name} ${customer.last_name}`], ["Contatti", `${customer.email} · ${customer.phone}`]].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[var(--line)] pb-3"><dt className="text-[var(--muted-ink)]">{k}</dt><dd className="text-right capitalize-first">{v}</dd></div>
                  ))}
                </dl>
                <p className="mt-4 text-xs text-[var(--muted-ink)]">Il pagamento avviene presso il centro. Nessun pagamento online.</p>
                {error && <p role="alert" className="mt-4 text-sm text-red-700" data-testid="booking-error">{error}</p>}
                <div className="mt-6 flex gap-3">
                  <button className="btn-primary" onClick={confirm} disabled={busy || !sel.slot} data-testid="booking-confirm">{busy ? "Conferma in corso…" : "Conferma prenotazione"}</button>
                  {!sel.slot && <button className="btn-outline" onClick={() => go(2)}>Scegli un altro orario</button>}
                </div>
              </div>
            )}
          </>
        )}
      </Container>
    </>
  );
}
